import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { MapContainer, TileLayer, Polygon, CircleMarker, Polyline, useMap, useMapEvents } from 'react-leaflet';
import { motion } from 'framer-motion';
import { Crosshair, Undo2, Trash2, MapPin, Search, Loader2 } from 'lucide-react';
import { AddressAutocomplete } from './AddressAutocomplete';
// Map-based area calculator. Users tap to drop vertices on a satellite view
// of their property and the component computes the enclosed area in square
// feet. Feeds a depth-based volume calculator upstream.
//
// Tiles: Esri World Imagery (free, attribution required, no API key).
//
// Area math: shoelace formula in a local equirectangular projection.
// Accurate to <0.5% for parcel-sized polygons in mid-latitudes.
const ESRI_TILE_URL = 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}';
const ESRI_ATTR = 'Tiles &copy; Esri &mdash; Source: Esri, Maxar, Earthstar Geographics';
// Default center: Tacoma. Same constant as the delivery zones page.
const DEFAULT_CENTER: [number, number] = [47.2073, -122.197];
const EARTH_RADIUS_M = 6371008.8;
function polygonAreaSqFt(latLngs: [number, number][]): number {
  if (latLngs.length < 3) return 0;
  const latRad = latLngs[0][0] * (Math.PI / 180);
  const cosLat = Math.cos(latRad);
  // Project to local meters using equirectangular approximation around the
  // first vertex. Accurate enough for sub-hectare parcels.
  const pts = latLngs.map(([lat, lon]) => [lon * (Math.PI / 180) * cosLat * EARTH_RADIUS_M, lat * (Math.PI / 180) * EARTH_RADIUS_M]);
  let area = 0;
  for (let i = 0; i < pts.length; i++) {
    const [x1, y1] = pts[i];
    const [x2, y2] = pts[(i + 1) % pts.length];
    area += x1 * y2 - x2 * y1;
  }
  const sqMeters = Math.abs(area) / 2;
  return sqMeters * 10.7639104;
}
// Click handler to add vertices.
function ClickToAddVertex({
  onAdd
}: {
  onAdd: (latLng: [number, number]) => void;
}) {
  useMapEvents({
    click(e) {
      onAdd([e.latlng.lat, e.latlng.lng]);
    }
  });
  return null;
}
// Programmatically fly the map to a target lat/lng.
function FlyTo({
  position
}: {
  position: [number, number] | null;
}) {
  const map = useMap();
  useEffect(() => {
    if (position) {
      map.flyTo(position, 19, {
        duration: 1.2
      });
    }
  }, [position, map]);
  return null;
}
// Force Leaflet to recompute its size — same trick as DeliveryZones to
// avoid half-rendered tiles when the container mounts before sizing settles.
function MapInvalidator() {
  const map = useMap();
  useEffect(() => {
    const invalidate = () => {
      try {
        map.invalidateSize({
          pan: false,
          animate: false
        });
      } catch {
        // ignore
      }
    };
    const ts = [0, 100, 300, 700, 1500].map(ms => setTimeout(invalidate, ms));
    return () => ts.forEach(clearTimeout);
  }, [map]);
  return null;
}
type Props = {
  onAreaChange: (sqFt: number | null) => void;
  initialAddress?: string;
};
export function MapAreaCalculator({
  onAreaChange,
  initialAddress
}: Props) {
  const [vertices, setVertices] = useState<[number, number][]>([]);
  const [center, setCenter] = useState<[number, number]>(DEFAULT_CENTER);
  const [flyTarget, setFlyTarget] = useState<[number, number] | null>(null);
  const [address, setAddress] = useState(initialAddress || '');
  const [isLocating, setIsLocating] = useState(false);
  const sqFt = useMemo(() => polygonAreaSqFt(vertices), [vertices]);
  useEffect(() => {
    onAreaChange(vertices.length >= 3 ? sqFt : null);
  }, [sqFt, vertices.length, onAreaChange]);
  const addVertex = useCallback((p: [number, number]) => {
    setVertices(prev => [...prev, p]);
  }, []);
  const undoVertex = useCallback(() => {
    setVertices(prev => prev.slice(0, -1));
  }, []);
  const clearAll = useCallback(() => {
    setVertices([]);
  }, []);
  const useMyLocation = useCallback(() => {
    if (!navigator.geolocation) return;
    setIsLocating(true);
    navigator.geolocation.getCurrentPosition(pos => {
      const t: [number, number] = [pos.coords.latitude, pos.coords.longitude];
      setCenter(t);
      setFlyTarget(t);
      setIsLocating(false);
    }, () => {
      setIsLocating(false);
    }, {
      enableHighAccuracy: true,
      timeout: 8000
    });
  }, []);
  return <div className="space-y-3" data-id="element-933">
      {/* Address search + locate me */}
      <div className="flex gap-2" data-id="element-934">
        <AddressAutocomplete value={address} onChange={setAddress} onSelect={s => {
        const t: [number, number] = [s.lat, s.lon];
        setCenter(t);
        setFlyTarget(t);
      }} placeholder="Search your address to zoom in…" variant="light" containerClassName="flex-1" leftIcon={<Search className="w-4 h-4 text-stone-500" data-id="element-936" />} inputClassName="w-full pl-10 pr-3 py-2.5 rounded-lg text-sm bg-white dark:bg-[#252525] border border-stone-200 dark:border-[#555555] text-stone-900 dark:text-white placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-brand-orange" data-id="element-935" />
        <button type="button" onClick={useMyLocation} disabled={isLocating} className="shrink-0 inline-flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-lg bg-white dark:bg-[#252525] border border-stone-200 dark:border-[#555555] text-stone-700 dark:text-stone-200 hover:bg-stone-50 dark:hover:bg-[#2f2f2f] text-xs font-bold transition-colors" aria-label="Use my location" title="Use my location" data-id="element-937">
          {isLocating ? <Loader2 className="w-4 h-4 animate-spin" data-id="element-938" /> : <Crosshair className="w-4 h-4" data-id="element-939" />}
          <span className="hidden sm:inline" data-id="element-940">Locate</span>
        </button>
      </div>

      {/* Map */}
      <div className="relative rounded-xl overflow-hidden border border-stone-200 dark:border-[#555555] h-[320px] md:h-[380px] bg-stone-100 dark:bg-[#1f1f1f]" data-id="element-941">
        <MapContainer center={center} zoom={18} maxZoom={20} scrollWheelZoom className="w-full h-full" zoomControl data-id="element-942">
          <TileLayer attribution={ESRI_ATTR} url={ESRI_TILE_URL} maxZoom={20} data-id="element-943" />
          <MapInvalidator data-id="element-944" />
          <FlyTo position={flyTarget} data-id="element-945" />
          <ClickToAddVertex onAdd={addVertex} data-id="element-946" />

          {/* Polygon fill once we have 3+ vertices */}
          {vertices.length >= 3 && <Polygon positions={vertices} pathOptions={{
          color: '#1DB954',
          weight: 2,
          fillColor: '#1DB954',
          fillOpacity: 0.25
        }} data-id="element-947" />}
          {/* Edge polyline while drawing first two vertices */}
          {vertices.length === 2 && <Polyline positions={vertices} pathOptions={{
          color: '#1DB954',
          weight: 2,
          dashArray: '4 4'
        }} data-id="element-948" />}
          {/* Vertex markers */}
          {vertices.map((v, i) => <CircleMarker key={i} center={v} radius={6} pathOptions={{
          color: '#ffffff',
          weight: 2,
          fillColor: '#1DB954',
          fillOpacity: 1
        }} data-id="element-949" />)}
        </MapContainer>

        {/* Live area readout */}
        <div className="absolute top-3 left-3 right-3 flex items-start justify-between gap-3 pointer-events-none" data-id="element-950">
          <div className="pointer-events-auto bg-black/70 backdrop-blur-md text-white rounded-lg px-3 py-2 text-xs font-bold shadow-lg" data-id="element-951">
            {vertices.length < 3 ? <span data-id="element-952">
                Tap to add corners ·{' '}
                <span className="text-[#6FE39A]" data-id="element-953">{vertices.length}/3+</span>
              </span> : <span data-id="element-954">
                <span className="text-[#6FE39A]" data-id="element-955">
                  {Math.round(sqFt).toLocaleString()} sq ft
                </span>{' '}
                · {vertices.length} corners
              </span>}
          </div>
          {vertices.length > 0 && <div className="pointer-events-auto flex gap-1.5" data-id="element-956">
              <button type="button" onClick={undoVertex} className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-black/70 backdrop-blur-md text-white hover:bg-black/80 transition-colors shadow-lg" aria-label="Undo last vertex" title="Undo" data-id="element-957">
                <Undo2 className="w-3.5 h-3.5" data-id="element-958" />
              </button>
              <button type="button" onClick={clearAll} className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-black/70 backdrop-blur-md text-white hover:bg-red-500/80 transition-colors shadow-lg" aria-label="Clear all vertices" title="Clear" data-id="element-959">
                <Trash2 className="w-3.5 h-3.5" data-id="element-960" />
              </button>
            </div>}
        </div>

        {/* Helper hint along the bottom */}
        {vertices.length === 0 && <motion.div initial={{
        opacity: 0,
        y: 8
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.4
      }} className="absolute bottom-3 left-3 right-3 pointer-events-none flex justify-center" data-id="element-961">
            <div className="bg-brand-orange/95 text-white text-[11px] font-bold px-3 py-1.5 rounded-full shadow-lg flex items-center gap-1.5" data-id="element-962">
              <MapPin className="w-3 h-3" data-id="element-963" />
              Tap the corners of your project area
            </div>
          </motion.div>}
      </div>
    </div>;
}